import type { RemoteFileClient } from "@/clients/remote-file.client";
import { getPlatformRuntime, isNativeApp } from "@/platform";
import i18n from "i18next";
import toast from "react-hot-toast";
import { createActionError } from "@/client-error";

let _remoteFiles: RemoteFileClient | undefined;

export const configureFileDownloads = (client: RemoteFileClient): void => {
  _remoteFiles = client;
};

const remoteFiles = (): RemoteFileClient => {
  if (!_remoteFiles) {
    throw new Error("File downloads used before controller construction");
  }
  return _remoteFiles;
};

export const downloadFile = async (
  params:
    | {
        type: "base64";
        base64: string;
        fileName: string;
      }
    | {
        type: "url";
        url: string;
        fileName: string;
        isLargeFile?: boolean; // Optional flag to force streaming download
      }
    | {
        type: "txt";
        text: string;
        fileName: string;
      }
    | {
        type: "file";
        file: File;
        fileName: string;
      },
) => {
  if (isNativeApp()) {
    switch (params.type) {
      case "file": {
        const base64 = await _readFileAsDataUrl(params.file);
        await downloadFile({
          type: "base64",
          base64,
          fileName: params.fileName,
        });
        break;
      }

      case "base64":
        await getPlatformRuntime()
          .files.writeCacheFile(`/${params.fileName}`, params.base64)
          .then((uri) =>
            getPlatformRuntime().share({ url: uri, title: params.fileName }),
          );
        break;

      case "url": {
        if (params.isLargeFile) {
          const toastId = toast.loading(i18n.t("utils.downloadingFile"));
          let downloadedUri: string | undefined;
          try {
            const { largeFileDownloader } =
              await import("@/clients/large-file-downloader.plugin");
            const result = await largeFileDownloader.downloadFile({
              url: params.url,
              fileName: params.fileName,
              openFile: false, // We'll handle sharing separately
            });
            downloadedUri = result.uri;
          } catch (error) {
            console.error(
              "Large-file download plugin failed, falling back to base64:",
              error,
            );
            // Fall through to the base64 method if plugin fails
          } finally {
            toast.dismiss(toastId);
          }

          if (downloadedUri) {
            // Only a failed DOWNLOAD may fall through to the base64 retry.
            // A rejected share (e.g. the user dismissing the Android share
            // sheet) used to fall through too, downloading and sharing the
            // same file a second time.
            try {
              await getPlatformRuntime().share({
                url: downloadedUri,
                title: params.fileName,
              });
            } catch {
              // Share canceled — the file is already downloaded.
            }
            return;
          }
        }

        // Fallback to base64 method for iOS and if plugin fails
        const result = await remoteFiles().download(params.url);
        if ("error" in result) {
          throw createActionError(result.error);
        }
        const base64 = await _readFileAsDataUrl(result.value);
        await downloadFile({
          type: "base64",
          base64,
          fileName: params.fileName,
        });
        break;
      }

      case "txt":
        await downloadFile({
          type: "base64",
          base64: Buffer.from(params.text).toString("base64"),
          fileName: params.fileName,
        });
        break;
    }

    return;
  }

  switch (params.type) {
    case "file": {
      const url = URL.createObjectURL(params.file);
      await downloadFile({
        type: "url",
        url,
        fileName: params.fileName,
      });
      break;
    }

    case "base64":
      break;

    case "url": {
      const downloadLink = document.createElement("a");
      downloadLink.href = params.url;
      downloadLink.download = params.fileName;

      document.body.appendChild(downloadLink);
      downloadLink.click();

      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(params.url);
      break;
    }

    case "txt": {
      const blob = new Blob([params.text], { type: "text/plain" });

      await downloadFile({
        type: "url",
        url: URL.createObjectURL(blob),
        fileName: params.fileName,
      });
      break;
    }
  }
};

const _readFileAsDataUrl = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(reader.error || new Error("File read failed"));
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        reject(new Error("File reader returned a non-string result"));
        return;
      }
      resolve(reader.result);
    };
    reader.readAsDataURL(file);
  });
